import React, { createContext, useEffect } from "react";
import GenieDrawer from "../components/special/genie-drawer/GenieDrawer";
import {
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import Orders from "./pages/orders/Orders";
import Genie404 from "../components/special/genie-404/Genie404";
import Terms from "./pages/terms/Terms";
import { useAuth0 } from "@auth0/auth0-react";
import { Nullable } from "../utils/Nullable";
import Products from "./pages/products/Products";
import { User } from "../types/api/User";
import Authentication from "./create/Authentication";
import CreateOrg from "./details/CreateOrg";
import MerchantChecklist from "./pages/welcome/MerchantChecklist";
import Storefront from "./store/storefront/Storefront";
import Catalog from "./store/catalog/Catalog";
import Pdp from "./store/pdp/Pdp";
import Onboarding from "./pages/onboarding/Onboarding";
import ProductList from "./pages/productList/ProductList";
import Checkout from "./store/checkout/Checkout";
import Billing from "./store/checkout/billing/Billing";
// import Payment from "./store/checkout/payment/Payment";
import GenieSpinner from "../components/core/genie-spinner/GenieSpinner";
import CustomerOrders from "./store/orders/CustomerOrders";
import useUser from "../queries/routes/useUser";
import InviteBuyer from "./invite/InviteBuyer";
import Invites from "./pages/invites/Invites";
import BuyerRedirect from "./details/BuyerRedirect";
import All from "./store/all/All";
import Customers from "./pages/customers/Customers";
import AdminCustomerOrders from "./pages/customers/AdminCustomerOrders";
// import PlaidLink from "../components/integrations/PlaidLink";
import StripeConnect from "./organization/StripeConnect";
import CheckoutReturn from "./store/checkout/CheckoutReturn";
import Logout from "./Logout";

export const SecurityContext = createContext<{
  accessToken: Nullable<string>;
  user: Nullable<User>;
  isSecurityContextLoading: boolean;
}>({
  accessToken: null,
  user: null,
  isSecurityContextLoading: false,
});

function Base() {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [accessToken, setAccessToken] = React.useState("");
  const [user] = React.useState<Nullable<User>>(null);
  const [isSecurityContextLoading, setIsSecurityContextLoading] =
    React.useState<boolean>(false);
  const navigate = useNavigate();
  let match = useMatch("/app/:org_id/:link");
  const location = useLocation();
  const { data: userData } = useUser(isAuthenticated);

  useEffect(() => {
    const fetchAccessToken = async () => {
      if (isAuthenticated && !isLoading && userData) {
        try {
          setIsSecurityContextLoading(true);
          const accessToken = await getAccessTokenSilently();
          setAccessToken(accessToken);

          // Check for invite_org_id in localStorage first - handle invite redirects before other logic
          const inviteOrgId = localStorage.getItem('invite_org_id');
          if (inviteOrgId) {

            if (userData && (!userData.organizations || userData.organizations.length === 0)) {
              navigate("/details");
              return;
            } else {
              console.log("Base: redirecting to invited storefront", inviteOrgId);
              navigate(`/storefront/${inviteOrgId}`);
              // Clear the invite_org_id from localStorage after redirecting
              localStorage.removeItem('invite_org_id');
              return; // Exit early, don't proceed with other redirects
            }
          }

          if (
            !match?.params.org_id &&
            !location.pathname.startsWith("/storefront") &&
            !location.pathname.startsWith("/app") &&
            !location.pathname.startsWith("/invite") &&
            userData.organizations &&
            userData.organizations.length > 0
          ) {
            const buyerOrg = userData.purchasing_organizations.find((org) => {
              return org.organization.selected_type === "Buyer";
            });
            if (buyerOrg) {
              const notMe = userData.purchasing_organizations.find((org) => {
                return org.organization.id !== userData.organizations[0].id;
              });
              if (notMe?.organization.id) {
                navigate(`/storefront/${notMe.organization.id}`);
              } else {
                navigate("/storefront/all");
              }
            } else {
              navigate(`/app/${userData.organizations[0].id}/orders`);
            }
          }
        } catch (error) {
          setIsSecurityContextLoading(false);
        }
      }
    };
    if (userData) {
      fetchAccessToken();
    }
  }, [
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    location.pathname,
    match?.params.org_id,
    navigate,
    userData,
  ]);

  return (
    <SecurityContext.Provider
      value={{
        isSecurityContextLoading,
        accessToken,
        user,
      }}
    >
      <Routes>
        <Route path={"app/:org_id"} element={<GenieDrawer />}>
          <Route path="orders" element={<Orders />} />
          <Route path="welcome" element={<MerchantChecklist />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route
            path="organization/onboarding/stripe-connect"
            element={<StripeConnect />}
          />
          <Route path="invites" element={<Invites />} />
          <Route path="terms" element={<Terms />} />
          <Route path="products" element={<ProductList />} />
          <Route path="products/:id" element={<Products />} />
          <Route path="customers">
            <Route path="" element={<Customers />} />
            <Route path=":customer_id" element={<AdminCustomerOrders />} />
          </Route>
          {/* <Route path="business-info" element={<PlaidLink />} /> */}
          <Route path="" element={<GenieSpinner />} />
          <Route path="*" element={<Genie404 />} />
        </Route>
        <Route path="storefront" element={<Storefront />}>
          <Route path={"all"} element={<All />} />
          <Route path={":org_id"}>
            <Route path="" element={<Catalog />} />
            <Route path="not-found" element={<Genie404 />} />
            <Route path="product/:product_id" element={<Pdp />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="billing" element={<Billing />} />
            <Route path="checkout/return" element={<CheckoutReturn />} />
            <Route path="orders" element={<CustomerOrders />} />
            <Route path="*" element={<Genie404 />} />
          </Route>
          <Route path="*" element={<Genie404 />} />
        </Route>
        <Route path={"/"} element={<Authentication />} />
        <Route path={"/logout"} element={<Logout />} />
        <Route path={"/details-buyer"} element={<BuyerRedirect />} />
        <Route path={"/invite/:invite_id"} element={<InviteBuyer />} />
        <Route path={"/details"} element={<CreateOrg />} />
        <Route path="*" element={<Genie404 />} />
      </Routes>
    </SecurityContext.Provider>
  );
}

export default Base;
